"use client";

import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  login as loginAPI,
  register as registerAPI,
  logout as logoutAPI,
  getUser as getUserAPI,
  setAuthToken,
  removeAuthToken,
  getAuthToken,
  verifyEmail as verifyEmailAPI,
  resendEmailVerification as resendEmailVerificationAPI,
} from "@/lib/utils";

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  apartment?: string;
  gender?: string;
  age?: number;
  dob?: string;
  email_verified_at?: string | null;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requires2fa: boolean;
  twoFactorPhone: string | null;
}

type AuthAction =
  | { type: "LOGIN_START" }
  | { type: "LOGIN_SUCCESS"; payload: { user: User; token: string } }
  | { type: "LOGIN_FAILURE"; payload: string }
  | { type: "LOGIN_2FA_REQUIRED"; payload: { phone: string } }
  | { type: "LOGOUT" }
  | { type: "REGISTER_START" }
  | { type: "REGISTER_SUCCESS"; payload: { user: User; token: string } }
  | { type: "REGISTER_FAILURE"; payload: string }
  | { type: "SET_USER"; payload: User }
  | { type: "CLEAR_ERROR" }
  | { type: "SET_LOADING"; payload: boolean };

const AuthContext = createContext<{
  state: AuthState;
  login: (username: string, password: string) => Promise<void>;
  register: (userData: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  sendOtp: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, otp: string) => Promise<void>;
  verifyEmail: (email: string, otp: string) => Promise<void>;
  resendEmailVerification: (email: string) => Promise<void>;
  clearError: () => void;
} | null>(null);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case "LOGIN_2FA_REQUIRED":
      return {
        ...state,
        requires2fa: true,
        twoFactorPhone: action.payload.phone,
        isLoading: false,
        error: null,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case "REGISTER_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "REGISTER_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "REGISTER_FAILURE":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case "SET_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    requires2fa: false,
    twoFactorPhone: null,
  });

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      console.log("Checking auth on mount, token:", token ? "exists" : "none");
      if (token) {
        try {
          const userData = await getUserAPI();
          console.log("User data received:", userData);
          dispatch({ type: "SET_USER", payload: userData.user });
        } catch (error) {
          console.error("Auth check failed:", error);
          // Token is invalid, remove it
          removeAuthToken();
          dispatch({ type: "LOGOUT" });
        }
      } else {
        // No token, set loading to false
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    dispatch({ type: "LOGIN_START" });
    try {
      const response = await loginAPI({ username: email, password });
      const { user, token } = response;

      setAuthToken(token);
      dispatch({ type: "LOGIN_SUCCESS", payload: { user, token } });
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage =
        error.message ||
        "Login failed. Please check your credentials and try again.";
      dispatch({ type: "LOGIN_FAILURE", payload: errorMessage });
      throw error;
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone?: string;
  }) => {
    dispatch({ type: "REGISTER_START" });
    try {
      const response = await registerAPI(userData);
      const { user, token } = response;

      setAuthToken(token);
      dispatch({ type: "REGISTER_SUCCESS", payload: { user, token } });
    } catch (error: any) {
      console.error("Registration error:", error);
      const errorMessage =
        error.message ||
        "Registration failed. Please check your information and try again.";
      dispatch({ type: "REGISTER_FAILURE", payload: errorMessage });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutAPI();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      removeAuthToken();
      dispatch({ type: "LOGOUT" });
    }
  };

  const sendOtp = async (phone: string) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/send-login-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      const data = await response.json();
      console.log('OTP sent successfully:', data);
    } catch (error: any) {
      console.error("Send OTP error:", error);
      const errorMessage = error.message || "Failed to send OTP. Please try again.";
      dispatch({ type: "LOGIN_FAILURE", payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const verifyOtp = async (phone: string, otp: string) => {
    dispatch({ type: "LOGIN_START" });
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/verify-login-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, otp }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid OTP');
      }

      const data = await response.json();
      const { user, token } = data;

      setAuthToken(token);
      dispatch({ type: "LOGIN_SUCCESS", payload: { user, token } });
    } catch (error: any) {
      console.error("OTP verification error:", error);
      const errorMessage = error.message || "Invalid OTP. Please try again.";
      dispatch({ type: "LOGIN_FAILURE", payload: errorMessage });
      throw error;
    }
  };

  const verifyEmail = async (email: string, otp: string) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const response = await verifyEmailAPI(email, otp);
      const { user } = response;
      
      // Update user data
      dispatch({ type: "SET_USER", payload: user });
      return response;
    } catch (error: any) {
      console.error("Email verification error:", error);
      const errorMessage = error.message || "Verification failed. Please try again.";
      dispatch({ type: "LOGIN_FAILURE", payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const resendEmailVerification = async (email: string) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      await resendEmailVerificationAPI(email);
    } catch (error: any) {
      console.error("Resend verification error:", error);
      const errorMessage = error.message || "Failed to resend verification. Please try again.";
      dispatch({ type: "LOGIN_FAILURE", payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        login,
        register,
        logout,
        sendOtp,
        verifyOtp,
        verifyEmail,
        resendEmailVerification,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
